export type SidebarInboxMode = 'chats' | 'inbox';

const STORAGE_PREFIX = 'imai:sidebar-inbox-mode:';

export function getSidebarInboxModeStorageKey(orgSlug: string): string {
  return `${STORAGE_PREFIX}${orgSlug}`;
}

/** Anything but an explicit "inbox" keeps the ordinary Chats sidebar. */
export function parseSidebarInboxMode(
  raw: string | null | undefined
): SidebarInboxMode {
  return raw === 'inbox' ? 'inbox' : 'chats';
}

export function getInboxRowTimestamp(row: {
  updatedAt?: number;
  _creationTime: number;
}): number {
  return row.updatedAt ?? row._creationTime;
}

/**
 * Merges the viewer's own paginated chats with the small set of followed
 * chats. A followed chat only appears once the loaded own pages reach its
 * activity time (or every page is loaded), so it never jumps ahead of rows
 * that are still unloaded. Chats already shown in Priority are left out.
 */
export function mergeInboxActivityRows<
  Row extends { _id: string; updatedAt?: number; _creationTime: number },
>(args: {
  own: ReadonlyArray<Row>;
  followed: ReadonlyArray<Row>;
  priorityThreadIds: ReadonlySet<string>;
  isDone: boolean;
}): Row[] {
  const oldestLoaded = args.own.reduce<number | null>((oldest, row) => {
    const at = getInboxRowTimestamp(row);
    return oldest === null || at < oldest ? at : oldest;
  }, null);
  const seen = new Set<string>();
  const merged: Row[] = [];
  const push = (row: Row) => {
    if (seen.has(row._id) || args.priorityThreadIds.has(row._id)) return;
    seen.add(row._id);
    merged.push(row);
  };
  args.own.forEach(push);
  for (const row of args.followed) {
    if (
      args.isDone ||
      (oldestLoaded !== null && getInboxRowTimestamp(row) >= oldestLoaded)
    ) {
      push(row);
    }
  }
  return merged.sort(
    (left, right) => getInboxRowTimestamp(right) - getInboxRowTimestamp(left)
  );
}

export type InboxPriorityReason =
  | 'needs_input'
  | 'mention'
  | 'failed'
  | 'unread'
  | 'attention';

type MarkAllReadEntry = {
  kind: 'thread' | 'item';
  reason: string;
  thread?: { _id: string };
  recipientId?: string;
};

/** Chats "Mark all as read" may clear; open questions stay until answered. */
export function getMarkAllReadThreadIds(
  entries: ReadonlyArray<MarkAllReadEntry>
): string[] {
  return entries.flatMap((entry) =>
    entry.kind === 'thread' &&
    entry.thread &&
    (entry.reason === 'failed' || entry.reason === 'unread')
      ? [entry.thread._id]
      : []
  );
}

/**
 * Mentions, connector approvals and reconnects clear at their source, so
 * only notifications and failed or unread chats count as targets.
 */
export function hasMarkAllReadTargets(
  entries: ReadonlyArray<MarkAllReadEntry>
): boolean {
  return (
    getMarkAllReadThreadIds(entries).length > 0 ||
    entries.some((entry) => entry.kind === 'item' && entry.recipientId)
  );
}

export type InboxItemSource =
  | 'notification'
  | 'channel_mention'
  | 'thread_mention'
  | 'connector_approval'
  | 'connector_reauth'
  | 'workspace_invitation';

/** Localized two-line text for a non-chat Priority row. */
export function getInboxItemText(args: {
  source: InboxItemSource;
  title: string;
  body?: string;
  count?: number;
  toolkitName?: string;
  copy: {
    mentionedYou: string;
    mentionedYouCount: string;
    mentionedYouInThread: string;
    approvalNeeded: string;
    reconnectConnector: string;
    invitedYou: string;
    invitedYouBy: string;
  };
}): { title: string; body: string | null } {
  const { copy } = args;
  switch (args.source) {
    case 'channel_mention':
      return {
        title: `#${args.title}`,
        body:
          args.count && args.count > 1
            ? copy.mentionedYouCount.replace('{count}', String(args.count))
            : copy.mentionedYou,
      };
    case 'thread_mention':
      return {
        title: `#${args.title}`,
        body: args.body
          ? `${copy.mentionedYouInThread} · ${args.body}`
          : copy.mentionedYouInThread,
      };
    case 'connector_approval':
      return {
        title: args.title || args.toolkitName || copy.approvalNeeded,
        body: args.body
          ? `${copy.approvalNeeded} · ${args.body}`
          : copy.approvalNeeded,
      };
    case 'connector_reauth':
      return {
        title: copy.reconnectConnector.replace(
          '{name}',
          args.toolkitName ?? ''
        ),
        body: args.title || null,
      };
    case 'workspace_invitation':
      return {
        title: args.title,
        body: args.body
          ? copy.invitedYouBy.replace('{name}', args.body)
          : copy.invitedYou,
      };
    default:
      return { title: args.title, body: args.body ?? null };
  }
}

/**
 * Header bell toggles the mode. The collapsed rail always opens the inbox
 * and expands the sidebar so the swapped body is visible.
 */
export function getBellToggleResult(args: {
  mode: SidebarInboxMode;
  collapsed: boolean;
}): { mode: SidebarInboxMode; expandSidebar: boolean } {
  if (args.collapsed) return { mode: 'inbox', expandSidebar: true };
  return {
    mode: args.mode === 'inbox' ? 'chats' : 'inbox',
    expandSidebar: false,
  };
}

/**
 * Second line of an inbox row. An open question shows the question, a
 * failure says so, otherwise the latest reply excerpt (Priority rows fall
 * back to their reason so they are never blank).
 */
export function getInboxRowPreview(args: {
  reason?: InboxPriorityReason;
  isAwaitingUserInput?: boolean;
  awaitingQuestion?: string;
  lastAssistantPreview?: string;
  threadStatus?: string;
  copy: {
    reasonNeedsInput: string;
    reasonFailed: string;
    reasonUnread: string;
  };
}): string | null {
  if (args.reason === 'needs_input' || args.isAwaitingUserInput) {
    return args.awaitingQuestion ?? args.copy.reasonNeedsInput;
  }
  if (args.reason === 'failed' || args.threadStatus === 'error') {
    return args.copy.reasonFailed;
  }
  if (args.lastAssistantPreview) return args.lastAssistantPreview;
  return args.reason === 'unread' ? args.copy.reasonUnread : null;
}

export function floorToMinute(timestamp: number): number {
  return Math.floor(timestamp / 60_000) * 60_000;
}

/**
 * Newest Priority activity time. The inbox re-marks itself seen whenever
 * this grows while it is on screen, so the bell only lights for activity
 * that arrived while the viewer was looking elsewhere.
 */
export function getNewestInboxActivityAt(
  entries: ReadonlyArray<{ at: number }> | undefined
): number | null {
  if (!entries || entries.length === 0) return null;
  return entries.reduce((newest, entry) => Math.max(newest, entry.at), 0);
}
